/**
 * InstructorDetailPage - 강사 상세 정보 페이지
 *
 * /centers/:centerId/instructors/:instructorId 경로로 접근하며,
 * 강사의 이름, 전화번호, 로그인 이메일, Studiomate ID, Firebase ID를 표시한다.
 */

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useCurrentUser } from "@/components/AuthProvider";
import { getInstructorDetail, type InstructorDetail } from "@/services/firestore/admin-instructors";

export function InstructorDetailPage() {
  const { centerId, instructorId } = useParams<{
    centerId: string;
    instructorId: string;
  }>();
  const user = useCurrentUser();

  const [instructor, setInstructor] = useState<InstructorDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchInstructor() {
      setIsLoading(true);
      setNotFound(false);

      try {
        const data = await getInstructorDetail(user.uid, Number(centerId), instructorId!);
        if (!cancelled) {
          if (data) {
            setInstructor(data);
          } else {
            setNotFound(true);
          }
        }
      } catch {
        if (!cancelled) {
          setNotFound(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchInstructor();

    return () => {
      cancelled = true;
    };
  }, [user.uid, centerId, instructorId]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Link
            to={`/centers/${centerId}/instructors`}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            &larr; 강사 목록
          </Link>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (notFound || !instructor) {
    return (
      <div className="space-y-6">
        <div>
          <Link
            to={`/centers/${centerId}/instructors`}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            &larr; 강사 목록
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-600">강사를 찾을 수 없습니다.</p>
        </div>
      </div>
    );
  }

  const infoRows: { label: string; value: string }[] = [
    { label: "전화번호", value: instructor.contact ?? "-" },
    { label: "로그인 이메일", value: instructor.email ?? "-" },
    {
      label: "Studiomate ID",
      value: instructor.studiomateMapping ? String(instructor.studiomateMapping.staffId) : "미연동",
    },
    { label: "Firebase ID", value: instructor.id },
  ];

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/centers/${centerId}/instructors`}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          &larr; 강사 목록
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{instructor.name}</h1>

        <dl className="divide-y divide-gray-200">
          {infoRows.map((row) => (
            <div
              key={row.label}
              className="py-3 flex justify-between sm:grid sm:grid-cols-3 sm:gap-4"
            >
              <dt className="text-sm font-medium text-gray-500">{row.label}</dt>
              <dd className="text-sm text-gray-900 sm:col-span-2">{row.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
